/**
 * Type-label per weergavemodus — de tekstuele helft van de v3.1↔v4-schakelaar.
 *
 * Na de type-merge (fase 4) draagt elk gemigreerd concept een generiek
 * ArchiMate-4-type plus een `archimate31.*`-compat-stereotype met z'n
 * oorspronkelijke identiteit. Welke van die twee je als *type* te zien
 * krijgt, hangt af van de modus:
 *
 * - **v3.1** — het compat-label ("Bedrijfsproces"), géén badge. Ziet er
 *   exact uit zoals vandaag; dat is de hele bedoeling van de terugweg.
 * - **v4** — het generieke label ("Proces") plus een guillemet-badge
 *   («Business Process»). De badge is niet cosmetisch: zonder 'm zien vier
 *   soorten processen er identiek uit, en C260 §14.2 stelt de guillemet
 *   juist als standaardnotatie voor specialisaties.
 *
 * **Born-v4 concepten** (aangemaakt in v4-modus, dus zonder compat-
 * stereotype) tonen in béide modi het generieke label zonder badge. Bewust
 * eerlijk: een 3.1-smaak eruit afleiden uit relaties of nesting is niet
 * deterministisch en zou de schakelaar in de praktijk onomkeerbaar maken.
 * De modelleur kan er expliciet één toekennen via het AttributePanel.
 *
 * Zie `docs/architecture/archimate4-migratie.md` §4 fase 3.
 */
import { isCompatStereotype, LEGACY_TYPE_BY_COMPAT_STEREOTYPE } from "@ae/domain";
import type { DisplayMode } from "../state/displayMode";
import { getRegistry, resolveBilingual } from "./index";

export interface TypeLabelResult {
  /** Het label dat als type-aanduiding getoond wordt. */
  label: string;
  /** Guillemet-badge, alleen gevuld in v4-modus voor een gemigreerd concept.
   *  De aanroeper bepaalt zelf waar 'ie 'm rendert (boven de naam, in een
   *  aparte regel, als tooltip) — deze helper doet alleen de tekst. */
  badge?: string;
}

/** Het eerste compat-stereotype in de array, of `undefined`. Er hoort er
 *  hooguit één te zijn (een concept had vóór de merge maar één type). */
export function compatStereotypeOf(stereotypes: readonly string[] | undefined): string | undefined {
  return stereotypes?.find(isCompatStereotype);
}

/**
 * Type-label voor een concept, gegeven de weergavemodus.
 *
 * `typeId` mag een subtype-id zijn — die resolvet de registry zelf niet, dus
 * de aanroeper krijgt in dat geval de ruwe id terug (zelfde gedrag als de
 * bestaande `getElementType`-call-sites).
 */
export function typeLabelFor(
  concept: { typeId: string; stereotypes?: readonly string[] },
  lang: string,
  mode: DisplayMode,
): TypeLabelResult {
  const reg = getRegistry();
  const compat = compatStereotypeOf(concept.stereotypes);

  if (mode === "v31" && compat) {
    // Terugweg: toon het 3.1-type alsof de merge nooit gebeurd is. We nemen
    // het label van het OUDE element-type (dat blijft in de pack staan, zie
    // fase 4 — `fase1_active: false` maar wel resolvebaar) en niet dat van
    // het stereotype, zodat 3.1-modus letterlijk de oude tekst toont.
    const legacyTypeId = LEGACY_TYPE_BY_COMPAT_STEREOTYPE[compat];
    const legacyType = legacyTypeId ? reg.getElementType(legacyTypeId) : undefined;
    if (legacyType) return { label: resolveBilingual(legacyType.label, lang) };
    // Geen legacy-type meer in de pack (zou niet moeten kunnen): val terug op
    // het stereotype-label, dat dezelfde tekst draagt.
    const bundle = reg.getStereotype(compat);
    if (bundle) return { label: resolveBilingual(bundle.label, lang) };
  }

  const elementType = reg.getElementType(concept.typeId);
  const label = elementType ? resolveBilingual(elementType.label, lang) : concept.typeId;

  if (mode === "v4" && compat) {
    const bundle = reg.getStereotype(compat);
    // Het stereotype-label ís al de guillemet-vorm («Business Process»), dus
    // niet nóg een keer inpakken.
    const badge = bundle ? resolveBilingual(bundle.label, lang) : undefined;
    return badge ? { label, badge } : { label };
  }

  return { label };
}
